import { injectable, multiInject } from 'inversify';
import type { Component } from 'vue';
import type { FormField as FormFieldInterface } from '@shared/common/form/domain/form/field/base.form-field';
import FormFieldUnknownComponent from '@shared/common/form/application/component/FormFieldUnknownComponent.vue';
import { FormDependencyTypes } from '@shared/common/form/form.dependency-types';
import type { FormFieldComponents } from '@shared/common/form/application/type/form-field-components.type';

@injectable()
export class FormFieldViewRegistryService {
  // Service de registre pour découpler le domaine de Vue : on utilise une clé string (viewKey)
  // dans les modèles métier plutôt qu'un type de composant UI, puis on résout ici le rendu.
  private readonly formFieldTypeOfValueType: Record<string, string> = {
    string: 'text',
    number: 'number',
    boolean: 'boolean',
    Date: 'date',
    File: 'file',
  };

  public constructor(
    @multiInject(FormDependencyTypes.FormFieldViewKeyComponents)
    private readonly formFieldViewKeyComponents: FormFieldComponents[],
  ) {}

  public resolveFormFieldComponent<T>(field: FormFieldInterface<T>): Component {
    const formFieldComponents = this.getFlattenFormFieldComponents();
    if (field.viewKey && formFieldComponents[field.viewKey]) {
      return formFieldComponents[field.viewKey];
    }

    return (
      formFieldComponents[field.type || this.guessFormFieldTypeFromValue(field.value)] || FormFieldUnknownComponent
    );
  }

  private guessFormFieldTypeFromValue(value: FormFieldInterface<unknown>['value']): string {
    return (
      this.formFieldTypeOfValueType[typeof value === 'object' && value !== null ? value.constructor.name : 'nothing'] ||
      this.formFieldTypeOfValueType[typeof value] ||
      'unknown'
    );
  }

  private getFlattenFormFieldComponents(): FormFieldComponents {
    return this.formFieldViewKeyComponents.reduce(
      (acc, components) => ({
        ...acc,
        ...components,
      }),
      {} as FormFieldComponents,
    );
  }
}
